import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Building2,
  Loader2,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Calendar as CalendarIcon,
  User,
  Mail,
  Phone,
  ChevronLeft,
  ChevronRight,
  FileText,
  DollarSign,
  Settings,
  Image,
  Video,
  Link,
  Upload,
  Trash2,
  Save,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Calendar } from "../../components/ui/calendar";
import { Label } from "../../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminStudioBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("list");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchDate, setSearchDate] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Calendar view state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Settings state
  const [studioSettings, setStudioSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingMedia, setSavingMedia] = useState(false);
  const [mediaForm, setMediaForm] = useState({
    images: ["", ""],
    video: "",
    video_thumbnail: "",
  });

  useEffect(() => {
    fetchBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchDate, page]);

  useEffect(() => {
    if (activeTab === "calendar" && selectedDate) {
      fetchBookingsByDate(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate, activeTab]);

  useEffect(() => {
    if (activeTab === "settings") {
      fetchStudioSettings();
    }
  }, [activeTab]);

  const fetchStudioSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/studio-settings`);
      setStudioSettings(res.data);
      const media = res.data.media || { images: [], video: null };
      setMediaForm({
        images: [
          media.images?.[0] || "",
          media.images?.[1] || "",
        ],
        video: media.video || "",
        video_thumbnail: media.video_thumbnail || "",
      });
    } catch (error) {
      console.error("Error fetching studio settings:", error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveMediaSettings = async () => {
    setSavingMedia(true);
    try {
      const images = mediaForm.images.filter(url => url.trim() !== "");
      await axios.put(`${API}/admin/studio-settings/media`, {
        images,
        video: mediaForm.video.trim() || null,
        video_thumbnail: mediaForm.video_thumbnail.trim() || null,
      });
      toast.success("Studio media updated successfully");
      fetchStudioSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update media");
    } finally {
      setSavingMedia(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/studio-bookings?page=${page}&limit=20`;
      
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      if (searchDate) {
        url += `&date=${searchDate}`;
      }
      
      const res = await axios.get(url);
      setBookings(res.data.bookings || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load studio bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingsByDate = async (date) => {
    setCalendarLoading(true);
    try {
      const res = await axios.get(`${API}/admin/studio-bookings/by-date?date=${date}`);
      setCalendarData(res.data);
    } catch (error) {
      console.error("Error fetching bookings by date:", error);
    } finally {
      setCalendarLoading(false);
    }
  };

  const STATUS_LABELS = {
    confirmed: "Studio booking confirmed",
    completed: "Studio booking marked complete",
    cancelled: "Studio booking cancelled",
  };

  const updateStatus = (bookingId, status) => {
    const doUpdate = async () => {
      await axios.put(`${API}/admin/studio-bookings/${bookingId}/status?status=${status}`);
      toast.success(STATUS_LABELS[status] || `Booking ${status}`);
      fetchBookings();
      if (activeTab === "calendar") {
        fetchBookingsByDate(format(selectedDate, "yyyy-MM-dd"));
      }
    };

    if (status === "cancelled") {
      toast.warning("Cancel this studio booking?", {
        description: "This cannot be undone.",
        action: {
          label: "Cancel Booking",
          onClick: async () => {
            try {
              await doUpdate();
            } catch {
              toast.error("Failed to cancel booking");
            }
          },
        },
        cancel: { label: "Keep", onClick: () => {} },
      });
      return;
    }

    doUpdate().catch(() => toast.error("Failed to update booking"));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      confirmed: "bg-green-500/20 text-green-400",
      cancelled: "bg-red-500/20 text-red-400",
      completed: "bg-blue-500/20 text-blue-400"
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  const totalPages = Math.ceil(total / 20);

  const BookingCard = ({ booking }) => (
    <Card className="card-empire">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Guest Info */}
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-empire-gold/20 flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-empire-gold" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">{booking.guest_name}</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-400">
                  <span className="flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {booking.guest_email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {booking.guest_phone}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Booking Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
              <div>
                <span className="text-neutral-500 block">Booking #</span>
                <span className="text-empire-gold font-mono">{booking.booking_number}</span>
              </div>
              <div>
                <span className="text-neutral-500 block">Date</span>
                <span className="text-white font-medium">{booking.booking_date}</span>
              </div>
              <div>
                <span className="text-neutral-500 block">Time</span>
                <span className="text-white">{booking.start_time} - {booking.end_time}</span>
              </div>
              <div>
                <span className="text-neutral-500 block">Amount</span>
                <span className="text-empire-gold font-bold">KES {booking.total_amount?.toLocaleString()}</span>
              </div>
            </div>

            {/* Purpose */}
            {booking.purpose && (
              <div className="text-sm mb-2">
                <span className="text-neutral-500">Purpose: </span>
                <span className="text-neutral-300">{booking.purpose}</span>
              </div>
            )}

            {/* Notes */}
            {booking.notes && (
              <div className="text-sm bg-neutral-800/50 rounded-lg p-2 mt-2">
                <span className="text-neutral-500 flex items-center gap-1 mb-1">
                  <FileText className="w-3 h-3" /> Notes:
                </span>
                <span className="text-neutral-300">{booking.notes}</span>
              </div>
            )}
          </div>

          {/* Status & Actions */}
          <div className="flex flex-row md:flex-col items-center gap-3">
            {getStatusBadge(booking.status)}
            
            <div className="flex gap-2">
              {booking.status === "pending" && (
                <>
                  <Button
                    size="sm"
                    onClick={() => updateStatus(booking.id, "confirmed")}
                    className="btn-empire text-xs"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(booking.id, "cancelled")}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20 text-xs"
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </>
              )}
              {booking.status === "confirmed" && (
                <Button
                  size="sm"
                  onClick={() => updateStatus(booking.id, "completed")}
                  className="btn-empire text-xs"
                >
                  Mark Complete
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="admin-studio-bookings">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Studio Bookings</h1>
        <p className="text-neutral-400">Manage studio rental bookings</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white">{total}</h3>
            <p className="text-sm text-neutral-400">Total Bookings</p>
          </CardContent>
        </Card>

        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-yellow-400">
              {bookings.filter(b => b.status === "pending").length}
            </h3>
            <p className="text-sm text-neutral-400">Pending</p>
          </CardContent>
        </Card>

        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-green-400">
              {bookings.filter(b => b.status === "confirmed").length}
            </h3>
            <p className="text-sm text-neutral-400">Confirmed</p>
          </CardContent>
        </Card>

        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-empire-gold" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-empire-gold">
              KES {bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0).toLocaleString()}
            </h3>
            <p className="text-sm text-neutral-400">Total Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-neutral-900 border border-neutral-800">
          <TabsTrigger value="list" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            <Building2 className="w-4 h-4 mr-2" />
            List View
          </TabsTrigger>
          <TabsTrigger value="calendar" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar View
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* List View */}
        <TabsContent value="list">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                type="date"
                value={searchDate}
                onChange={(e) => { setSearchDate(e.target.value); setPage(1); }}
                className="input-empire pl-10 w-full md:w-auto"
              />
            </div>
            
            <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <TabsList className="bg-neutral-900 border border-neutral-800">
                <TabsTrigger value="all" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
                  All
                </TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
                  Pending
                </TabsTrigger>
                <TabsTrigger value="confirmed" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
                  Confirmed
                </TabsTrigger>
                <TabsTrigger value="completed" className="data-[state=active]:bg-empire-gold data-[state=active]:text-black">
                  Completed
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {searchDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchDate("")}
                className="border-neutral-700"
              >
                Clear Date
              </Button>
            )}
          </div>

          {/* Bookings List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <Card className="card-empire">
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                <p className="text-neutral-500">No studio bookings found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <BookingCard booking={booking} />
                </motion.div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-neutral-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-neutral-400">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-neutral-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <Card className="card-empire">
              <CardHeader>
                <CardTitle className="text-white">Select Date</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border border-neutral-800"
                  classNames={{
                    day_selected: "bg-empire-gold text-black hover:bg-empire-gold/90",
                    day_today: "bg-neutral-800 text-white",
                  }}
                />
              </CardContent>
            </Card>

            {/* Bookings for Selected Date */}
            <div className="lg:col-span-2">
              <Card className="card-empire h-full">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <span>Bookings for {format(selectedDate, "MMMM d, yyyy")}</span>
                    {calendarData && (
                      <Badge className="bg-empire-gold/20 text-empire-gold">
                        {calendarData.total_bookings} bookings
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {calendarLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
                    </div>
                  ) : calendarData && calendarData.bookings.length > 0 ? (
                    <>
                      {/* Summary Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-neutral-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-white">{calendarData.total_bookings}</div>
                          <div className="text-xs text-neutral-400">Total Bookings</div>
                        </div>
                        <div className="bg-neutral-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-white">{calendarData.total_hours}</div>
                          <div className="text-xs text-neutral-400">Total Hours</div>
                        </div>
                        <div className="bg-neutral-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-empire-gold">KES {calendarData.total_revenue?.toLocaleString()}</div>
                          <div className="text-xs text-neutral-400">Revenue</div>
                        </div>
                      </div>

                      {/* Bookings List */}
                      <div className="space-y-3">
                        {calendarData.bookings.map((booking) => (
                          <div
                            key={booking.id}
                            className="bg-neutral-800/30 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-neutral-500" />
                                <span className="text-white font-medium">{booking.guest_name}</span>
                              </div>
                              {getStatusBadge(booking.status)}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-sm">
                              <div>
                                <span className="text-neutral-500">Time:</span>
                                <span className="text-neutral-300 ml-1">{booking.start_time} - {booking.end_time}</span>
                              </div>
                              <div>
                                <span className="text-neutral-500">Amount:</span>
                                <span className="text-empire-gold ml-1">KES {booking.total_amount?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-neutral-500">Phone:</span>
                                <span className="text-neutral-300 ml-1">{booking.guest_phone}</span>
                              </div>
                            </div>
                            {booking.purpose && (
                              <div className="mt-2 text-sm">
                                <span className="text-neutral-500">Purpose:</span>
                                <span className="text-neutral-300 ml-1">{booking.purpose}</span>
                              </div>
                            )}
                            {booking.notes && (
                              <div className="mt-2 text-sm bg-neutral-800/50 rounded p-2">
                                <span className="text-neutral-500">Notes:</span>
                                <span className="text-neutral-300 ml-1">{booking.notes}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <CalendarIcon className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
                      <p className="text-neutral-500">No studio bookings for this date</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Settings View */}
        <TabsContent value="settings">
          {settingsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Video Settings (Main) */}
              <Card className="card-empire">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Video className="w-5 h-5 text-empire-gold" />
                    Studio Video (Main)
                  </CardTitle>
                  <p className="text-sm text-neutral-400">
                    Add a video URL for the main studio showcase.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-neutral-300">Video URL</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                        <Input
                          value={mediaForm.video}
                          onChange={(e) => setMediaForm({ ...mediaForm, video: e.target.value })}
                          className="input-empire pl-10"
                          placeholder="https://example.com/video.mp4"
                        />
                      </div>
                      {mediaForm.video && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setMediaForm({ ...mediaForm, video: "" })}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Video Preview */}
                  {mediaForm.video && (
                    <div className="mt-4">
                      <video
                        src={mediaForm.video}
                        className="w-full h-48 object-cover rounded-lg border border-neutral-800"
                        controls
                        poster={mediaForm.video_thumbnail || undefined}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  {/* Video Thumbnail */}
                  <div className="space-y-2 mt-4 pt-4 border-t border-neutral-800">
                    <Label className="text-neutral-300">Video Thumbnail (Poster)</Label>
                    <p className="text-xs text-neutral-500 mb-2">
                      Shows while video is loading. Recommended: same dimensions as video.
                    </p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                        <Input
                          value={mediaForm.video_thumbnail}
                          onChange={(e) => setMediaForm({ ...mediaForm, video_thumbnail: e.target.value })}
                          className="input-empire pl-10"
                          placeholder="https://example.com/thumbnail.jpg"
                        />
                      </div>
                      {mediaForm.video_thumbnail && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setMediaForm({ ...mediaForm, video_thumbnail: "" })}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    {/* Thumbnail Preview */}
                    {mediaForm.video_thumbnail && (
                      <div className="mt-2">
                        <img
                          src={mediaForm.video_thumbnail}
                          alt="Video thumbnail preview"
                          className="w-full h-32 object-cover rounded-lg border border-neutral-800"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Images Settings */}
              <Card className="card-empire">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-empire-gold" />
                    Studio Images
                  </CardTitle>
                  <p className="text-sm text-neutral-400">
                    Add 2 images displayed below the video.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[0, 1].map((index) => (
                    <div key={index} className="space-y-2">
                      <Label className="text-neutral-300">
                        Image {index + 1}
                      </Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                          <Input
                            value={mediaForm.images[index]}
                            onChange={(e) => {
                              const newImages = [...mediaForm.images];
                              newImages[index] = e.target.value;
                              setMediaForm({ ...mediaForm, images: newImages });
                            }}
                            className="input-empire pl-10"
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>
                        {mediaForm.images[index] && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              const newImages = [...mediaForm.images];
                              newImages[index] = "";
                              setMediaForm({ ...mediaForm, images: newImages });
                            }}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {/* Preview */}
                      {mediaForm.images[index] && (
                        <div className="mt-2">
                          <img
                            src={mediaForm.images[index]}
                            alt={`Studio preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-neutral-800"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Current Settings Info */}
              {studioSettings && (
                <Card className="card-empire lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Settings className="w-5 h-5 text-empire-gold" />
                      Current Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="p-4 bg-neutral-800/50 rounded-lg text-center">
                        <span className="text-neutral-500 block mb-1">Hourly Rate</span>
                        <span className="text-empire-gold font-semibold text-lg">
                          KES {studioSettings.hourly_rate?.toLocaleString()}
                        </span>
                      </div>
                      <div className="p-4 bg-neutral-800/50 rounded-lg text-center">
                        <span className="text-neutral-500 block mb-1">Video</span>
                        <span className="text-neutral-300 font-medium">
                          {studioSettings.media?.video ? "Uploaded" : "Not set"}
                        </span>
                      </div>
                      <div className="p-4 bg-neutral-800/50 rounded-lg text-center">
                        <span className="text-neutral-500 block mb-1">Images</span>
                        <span className="text-neutral-300 font-medium">
                          {studioSettings.media?.images?.filter(i => i).length || 0} / 2
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Save Button */}
              <div className="lg:col-span-2">
                <Button
                  onClick={saveMediaSettings}
                  disabled={savingMedia}
                  className="btn-empire"
                >
                  {savingMedia ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Media Settings
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
